/**
 * Copyright 2022-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import styled from "styled-components";
import {
  Box,
  FlexContainer,
  Select,
  Spacer,
  Text,
  theme,
} from "@nordcloud/gnui";
import {
  CloudResourcesFilterByInput,
  CloudResourcesKeyValueFilterByInput,
  CloudResourcesRangeFilterByInput,
  CloudResourcesSortFields,
  SortOrder,
} from "~/generated/graphql";
import { SortSelector } from "~/components/SortSelector";
import { ALL_OPTION, VM_TYPES, TABLE_LABELS } from "~/constants";
import { useQueryState } from "~/hooks";
import { isEmpty } from "~/tools";
import { ResourceQuery } from "../types";
import { Actions } from "./Actions";
import { ResourceTableState } from "./const";
import GenerateCloudResourceReport from "./GenerateReport/GenerateCloudResourceReport";
import { ResourceFiltersSidebar } from "./ResourceFiltersSidebar/ResourceFiltersSidebar";
import { ResourceSearch } from "./ResourcesSearch/ResourceSearch";

const sortOptions = [
  {
    value: CloudResourcesSortFields.Name,
    label: "Resource Name",
  },
  {
    value: CloudResourcesSortFields.CreatedAt,
    label: TABLE_LABELS.createdAt,
  },
  {
    value: CloudResourcesSortFields.Type,
    label: "Resource Type",
  },
  {
    value: CloudResourcesSortFields.Region,
    label: "Region",
  },
];

const showOptions = [
  { value: ALL_OPTION, label: "All" },
  { value: VM_TYPES, label: "VMs" },
];

type Props = {
  resourcesDetails: {
    id: string;
    cloudResourceId: string;
  }[];
  setResourcesSearchPage: (resourcesPage: ResourceTableState) => void;
  resourceType: string;
  setResourceType: (type: string) => void;
  selectedResourceIDs: string[];
  allResourcesSelected: boolean;
  filters: {
    filterBy?: CloudResourcesFilterByInput[];
    keyValueFilterBy?: CloudResourcesKeyValueFilterByInput[];
    rangeFilterBy?: CloudResourcesRangeFilterByInput[];
  };
};

export function ResourceListHeader({
  resourcesDetails,
  setResourcesSearchPage,
  setResourceType,
  resourceType,
  selectedResourceIDs,
  filters,
  allResourcesSelected,
}: Props) {
  const { queryState, updateQueryState } = useQueryState<ResourceQuery>();

  const handleShowSelection = (
    selected: { value: string; label: string } | null
  ) => {
    setResourceType(selected?.value || ALL_OPTION);
  };

  const handleSortDirection = (sortOrder: SortOrder) => {
    updateQueryState({
      ...queryState,
      order: sortOrder,
    });
  };

  const handleSortField = (sortField: CloudResourcesSortFields) => {
    updateQueryState({
      ...queryState,
      field: sortField,
    });
  };

  const StyledDropdown = styled.div`
    position: relative;
    width: 240px;
    & > div {
      width: 100%;
    }
  `;

  return (
    <Box boxStyle="lightGrey" spacing="spacing04">
      <FlexContainer justifyContent="space-between">
        <Text
          tag="div"
          mr={theme.spacing.spacing04}
          style={{ whiteSpace: "nowrap" }}
        >
          Show
        </Text>
        <StyledDropdown>
          <Select<{ value: string; label: string }>
            options={[
              {
                options: showOptions,
              },
            ]}
            value={showOptions.find((opt) => opt.value === resourceType)}
            onChange={handleShowSelection}
          />
        </StyledDropdown>
        <Spacer width={theme.spacing.spacing06} />
        <SortSelector<CloudResourcesSortFields>
          options={sortOptions}
          value={queryState.field ?? CloudResourcesSortFields.Name}
          direction={queryState.order ?? SortOrder.Asc}
          onSelect={handleSortField}
          onSortChange={handleSortDirection}
        />
        <Spacer width={theme.spacing.spacing06} />
        <ResourceSearch setResourcesSearchPage={setResourcesSearchPage} />
        <Spacer width={theme.spacing.spacing07} />
        <ResourceFiltersSidebar resourceType={resourceType} />
        <GenerateCloudResourceReport
          disabled={isEmpty(selectedResourceIDs) && !allResourcesSelected}
          filters={{
            filterBy: filters.filterBy as
              | CloudResourcesFilterByInput[]
              | undefined,
            keyValueFilterBy: filters.keyValueFilterBy as
              | CloudResourcesKeyValueFilterByInput[]
              | undefined,
            rangeFilterBy: filters.rangeFilterBy as
              | CloudResourcesRangeFilterByInput[]
              | undefined,
          }}
          selectedResourceIDs={selectedResourceIDs}
        />
        <Spacer width={theme.spacing.spacing08} />
        <Actions size="large" {...{ resourcesDetails }} />
      </FlexContainer>
    </Box>
  );
}
